<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { staticName } from '@/lib/asset';
import { t } from '~/i18n';

const { hideLabel = false } = defineProps<{ hideLabel?: boolean }>();

const containerRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

const ratioShapes = {
  '16:9': 'w-5 h-[11px]',
  '4:3': 'w-5 h-[15px]',
  '1:1': 'w-3.5 h-3.5',
  '3:4': 'w-3.5 h-[19px]',
  '4:5': 'w-3.5 h-[17px]',
  '9:16': 'w-3 h-[21px]',
  '3:1': 'w-6 h-2',
  '2:1': 'w-5 h-2.5',
  A4: 'w-3 h-[17px]',
};

const cardDefs = [
  {
    i18nKey: 'report',
    ratios: ['16:9', '4:3', '1:1'],
    bg: 'from-[#FECACA]/60 to-[#FECACA]/10',
    img: `${staticName}/static-assets/card-bg/16-9/07.webp`,
    imgClass: 'w-[100%] md:w-[60%] aspect-[16/9]',
    bottom: '0',
    delay: 0,
  },
  {
    i18nKey: 'social_post',
    ratios: ['1:1', '4:5', '9:16'],
    bg: 'from-[#E9D5FF]/60 to-[#E9D5FF]/10',
    img: `${staticName}/static-assets/card-bg/4-5/art.webp`,
    imgClass: 'w-[75%] md:w-[50%] aspect-square',
    bottom: '0',
    delay: 100,
  },
  {
    i18nKey: 'ppt_deck',
    ratios: ['16:9', '4:3'],
    bg: 'from-[#E5A97C]/60 to-[#E5A97C]/10',
    img: `${staticName}/static-assets/card-bg/9-16/Deck.webp`,
    imgClass: 'w-[90%] md:w-[80%] aspect-[16/9]',
    bottom: '0',
    delay: 200,
  },
  {
    i18nKey: 'newsletter',
    ratios: ['3:4', '4:5', 'A4'],
    bg: 'from-[#FFA05B]/60 to-[#FFA05B]/10',
    img: `${staticName}/static-assets/card-bg/9-16/ProductPost.webp`,
    imgClass: 'w-[60%] md:w-[60%] aspect-[9/16]',
    bottom: '-10%',
    delay: 300,
  },
  {
    i18nKey: 'print',
    ratios: ['A4', '3:4', '1:1'],
    bg: 'from-[#A7F4F3]/60 to-[#A7F4F3]/10',
    img: `${staticName}/static-assets/card-bg/9-16/compaign.webp`,
    imgClass: 'w-[50%] md:w-[70%] aspect-[9/16]',
    bottom: '0',
    delay: 400,
  },
  {
    i18nKey: 'banner',
    ratios: ['3:1', '2:1', '16:9'],
    bg: 'from-[#FD6EFA]/60 to-[#FD6EFA]/10',
    img: `${staticName}/static-assets/card-bg/16-9/06.webp`,
    imgClass: 'w-[100%] md:w-[50%] aspect-[3/1]',
    bottom: '0',
    delay: 500,
  },
] as const;

const cards = computed(() =>
  cardDefs.map((def) => ({
    ...def,
    title: t(`landing_home.cards.deliver.${def.i18nKey}`),
  }))
);

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true;
        }
      });
    },
    { threshold: 0.3 }
  );
  if (containerRef.value) {
    observer.observe(containerRef.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <div ref="containerRef" class="relative w-full h-full">
    <span
      v-if="!hideLabel"
      class="absolute top-4 left-4 font-boldonse text-sm text-black uppercase z-10"
    >
      Export
    </span>

    <div class="w-full h-full p-2 md:p-0">
      <div class="w-full h-full grid grid-cols-2 md:grid-cols-12 md:grid-rows-12 gap-2 md:gap-2">
        <div
          v-for="(card, index) in cards"
          :key="card.i18nKey"
          class="rounded-xl bg-gradient-to-b p-3 relative overflow-hidden aspect-square md:aspect-auto"
          :class="[
            card.bg,
            {
              'md:col-span-7 md:row-span-5': index === 0,
              'md:col-span-5 md:row-span-5 md:col-start-8': index === 1,
              'md:col-span-5 md:row-span-5 md:row-start-6': index === 2,
              'md:col-span-4 md:row-span-7 md:col-start-6 md:row-start-6': index === 3,
              'md:col-span-3 md:row-span-7 md:col-start-10 md:row-start-6': index === 4,
              'md:col-span-5 md:row-span-2 md:row-start-11': index === 5,
            },
          ]"
        >
          <div class="relative z-10">
            <p class="text-sm font-normal text-black/80 uppercase">{{ card.title }}</p>
            <div class="flex items-end gap-1.5 mt-1.5">
              <span
                v-for="ratio in card.ratios"
                :key="ratio"
                class="border border-black/20"
                :class="ratioShapes[ratio as keyof typeof ratioShapes]"
              />
            </div>
          </div>
          <img
            :src="card.img"
            :alt="`${card.title} design preview`"
            class="absolute right-0 translate-x-[14%] rotate-[5deg] object-cover shadow-xl card-img-enter"
            :class="[card.imgClass, { 'card-img-visible': isVisible }]"
            :style="{ bottom: card.bottom, transitionDelay: `${card.delay}ms` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-img-enter {
  transform: translateY(40px) translateX(var(--tw-translate-x, 0)) rotate(5deg);
  opacity: 0;
  transition:
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.card-img-visible {
  opacity: 1;
  transform: translateY(0) translateX(var(--tw-translate-x, 0)) rotate(5deg);
}
</style>
